import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box/Box";
import BackButton from "components/common/BackButton";
import ErrorPage from "components/common/ErrorPage";
import Feedback from "components/common/Feedback";
import Snackbar from "components/common/Snackbar";
import AboveProperty from "components/features/AboveProperty";
import PlanProgress from "components/features/AboveProperty/PlanView/PlansProgress";
import CorpDashboard from "components/features/Corp/CorpDashboard";
import IndividualProperty from "components/features/IndividualProperty";

import MyLists from "components/features/Corp/MyLists";
import MyPlanDetails from "components/features/MyPlanDetails";
import MyPlans from "components/features/MyPlans";
import Progress from "components/features/Progress";
import Recommendations from "components/features/Recommendations";
import UserView from "components/features/UserView";
import Dashboard from "components/features/dashboard";
import Library from "components/features/library";
import LibraryHallSearch from "components/features/library/Search";
import FunctionalArea from "components/features/library/functionalArea";
import PlanDetails from "components/features/planDetails";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { nestedTernaryCheckerCond1 } from "utils/CommonChecker";
import { messages } from "utils/CommonConstants";
import { decrypt, getInitialWizardTaken, getIsCorpUser } from "utils/CommonFunctions";
import { routes } from "utils/routes";
import { theme as MUITheme } from "../../styles/Theme";
import DialogsContextLayout from "./DialogsContextLayout";
import "./Main.scss";

const ErroBoundryRoute = () => {
  const location = useLocation();
  const isAdmin = useAppSelector((state) => state.app.isAdmin);
  const isSmeUser: any = useAppSelector((state) => state.app.isSmeUser);

  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const isDashboard = location.pathname.split("/")[1] === "";
  const isAboveProperty = useAppSelector((state) => state.app.isAbovePropertyUser);
  const isAbovePropertyUserFetched: any = useAppSelector((state) => state.app.isAbovePropertyUserFetched);
  const isAbovePropertyUser = isAbovePropertyUserFetched ? isAboveProperty : true;
  const isInitialOnboardingCompleted = getInitialWizardTaken();
  const initialWizard = useAppSelector((state) => state.onboardingResponse.initialWizard);
  const isStatus = useAppSelector((state) => state.filters?.isStatus);
  const defaultFilter = useAppSelector((state) => state.filters?.savedFilter?.default);

  const isRoutesLoad = () => {
    if (getIsCorpUser(decrypt("salt", localStorage.getItem("lobbyUserType")))) {
      return isStatus && defaultFilter?.length;
    }
    return isInitialOnboardingCompleted || initialWizard;
  };

  return (
    <>
      {isRoutesLoad() && (
        <ErrorBoundary fallback={<ErrorPage />}>
          <Box
            component="main"
            className={isImpersonated ? "impersonate-main" : "non-impersonated-main"}
            marginLeft={{ md: "137px", xs: "16px" }}
            marginRight={{
              md: nestedTernaryCheckerCond1(isDashboard, !isSmeUser && isAdmin, "32px", "0", "32px"),
              xs: "16px"
            }}
            paddingY={{ xs: "16px", md: "unset" }}
            sx={{
              borderTop:
                location.pathname.split("/")[1] !== "library" && !isMobileScreen
                  ? `1px solid ${tokens.Border.General.Tertiary}`
                  : "none"
            }}
          >
            <Snackbar />
            <Feedback form="updatePlan" message={messages.editSuccess} />
            {isMobileScreen && <BackButton label="Back" />}
            <Routes>
              <Route element={<DialogsContextLayout />}>
                <Route path={routes.ProgressInfo} element={<Progress />} />
                {isAdmin && (
                  <>
                    <Route path={routes.IndividualProperty} element={<IndividualProperty />} />
                    {/* <Route path={routes.InncodeUsers} element={<InncodeUsers />} /> */}
                    <Route path={routes.UserDashboard} element={<Dashboard />} />
                  </>
                )}
                {isAdmin && !isSmeUser && (
                  <>
                    <Route path={routes.MyLists} element={<MyLists />} />
                    <Route path={routes.Dashboard} element={<CorpDashboard label="My Dashboard" />} />
                  </>
                )}
                {isSmeUser && (
                  <>
                    <Route path={routes.Dashboard} element={<MyLists />} />
                    <Route path={routes.CorporateDashboard} element={<CorpDashboard label="Corporate Dashboard" />} />
                    <Route path={routes.MyLists} element={<MyLists />} />
                  </>
                )}
                {!isAdmin && (
                  <>
                    {isAbovePropertyUser && isAbovePropertyUserFetched ? (
                      <>
                        <Route path={routes.IndividualProperty} element={<IndividualProperty />} />
                        <Route path={routes.Dashboard} element={<AboveProperty />} />
                        <Route path={routes.UserDashboard} element={<Dashboard />} />
                      </>
                    ) : (
                      <Route path={routes.Dashboard} element={<Dashboard />} />
                    )}
                  </>
                )}
                <Route path={routes.Library} element={<Library />} />
                <Route path={routes.MyPlans} element={<MyPlans />} />
                <Route path={routes.Recommendations} element={<Recommendations />} />
                <Route path={routes.UserView} element={<UserView />} />

                <Route path={routes.FunctionalArea} element={<FunctionalArea />} />
                <Route path={routes.MyPlanDetails} element={<MyPlanDetails />}>
                  <Route path={routes.sectionDetails} element={<MyPlanDetails />}>
                    <Route path={routes.CardDetails} element={<MyPlanDetails />} />
                  </Route>
                </Route>
                <Route path={routes.LibraryHallSearch} element={<LibraryHallSearch />} />
                <Route path={routes.PlanDetails} element={<PlanDetails />}>
                  <Route path={routes.sectionDetails} element={<PlanDetails />}>
                    <Route path={routes.CardDetails} element={<PlanDetails />} />
                  </Route>
                </Route>
                <Route path={`${routes.PlanProgress}/:id`} element={<PlanProgress />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>
        </ErrorBoundary>
      )}
    </>
  );
};

export default ErroBoundryRoute;
